import { useEffect, useRef, useState } from 'react';

/**
 * Cloudflare Turnstile (CAPTCHA) widget — bot/abuse protection on the open
 * public signup path (CC-SPEC item 3). Mirrors the VeriBrief approach:
 * load the CF script, render the widget explicitly, surface the token to
 * the parent for `supabase.auth.signUp({ options: { captchaToken } })`.
 *
 * Operator config (NOT here): create a Turnstile site in Cloudflare, set
 * `VITE_TURNSTILE_SITE_KEY` (frontend), and enable CAPTCHA (provider
 * Turnstile, secret key) in Supabase → Auth → Attack Protection.
 */

const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SCRIPT_ID = 'cf-turnstile-script';
let scriptPromise: Promise<void> | null = null;

interface TurnstileApi {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  remove: (widgetId: string) => void;
  reset: (widgetId?: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  const pending = new Promise<void>((resolve, reject) => {
    if (window.turnstile) {
      resolve();
      return;
    }
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener(
        'load',
        () => {
          if (window.turnstile) resolve();
          else {
            existing.remove();
            reject(new Error('turnstile API unavailable'));
          }
        },
        { once: true },
      );
      existing.addEventListener(
        'error',
        () => {
          existing.remove();
          reject(new Error('turnstile script failed'));
        },
        { once: true },
      );
      return;
    }
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.turnstile) resolve();
      else {
        script.remove();
        reject(new Error('turnstile API unavailable'));
      }
    };
    script.onerror = () => {
      // Remove a failed script so a user-initiated retry creates a fresh
      // request instead of attaching listeners to an already-fired element.
      script.remove();
      reject(new Error('turnstile script failed'));
    };
    document.head.appendChild(script);
  });
  scriptPromise = pending;

  // A rejected module-level promise must not poison all later mounts/retries.
  void pending.catch(() => {
    if (scriptPromise === pending) scriptPromise = null;
  });
  return pending;
}

interface TurnstileProps {
  siteKey: string;
  onToken: (token: string) => void;
  onError?: (message: string) => void;
  onExpire?: () => void;
  className?: string;
}

export function Turnstile({ siteKey, onToken, onError, onExpire, className }: TurnstileProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [status, setStatus] = useState<
    'loading' | 'ready' | 'verified' | 'expired' | 'failed'
  >('loading');
  // Hold the latest callbacks in a ref so changing them doesn't tear down and
  // re-render the widget (which would reset the challenge). The ref updates
  // in a per-render effect (not during render) per the react-hooks/refs rule.
  const cbRef = useRef({ onToken, onError, onExpire });
  useEffect(() => {
    cbRef.current = { onToken, onError, onExpire };
  });

  useEffect(() => {
    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        setLoadFailed(false);
        setStatus('ready');
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token: string) => {
            setStatus('verified');
            cbRef.current.onToken(token);
          },
          'error-callback': () => {
            setLoadFailed(true);
            setStatus('failed');
            cbRef.current.onError?.('CAPTCHA check failed — please retry.');
          },
          'expired-callback': () => {
            setStatus('expired');
            cbRef.current.onExpire?.();
          },
        });
      })
      .catch(() => {
        if (!cancelled) {
          setLoadFailed(true);
          setStatus('failed');
          cbRef.current.onError?.('Could not load CAPTCHA. Check your connection and retry.');
        }
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // Widget already gone (script unloaded / double-invoke) — nothing to do.
        }
        widgetIdRef.current = null;
      }
    };
  }, [retryAttempt, siteKey]);

  return (
    <div className={className}>
      <div
        ref={containerRef}
        aria-label="Security verification"
        className="max-w-full overflow-x-auto"
      />
      <p
        className="mt-1 mb-0 font-ui text-xs text-text-tertiary"
        role="status"
        aria-live="polite"
      >
        {status === 'loading'
          ? 'Loading security verification…'
          : status === 'verified'
            ? 'Security verification complete.'
            : status === 'expired'
              ? 'Security verification expired. Complete it again.'
              : status === 'failed'
                ? 'Security verification is unavailable.'
                : 'Complete the security verification to continue.'}
      </p>
      {loadFailed && (
        <button
          type="button"
          className="mt-2 font-ui text-sm text-navy-700 underline underline-offset-2"
          onClick={() => {
            setLoadFailed(false);
            setStatus('loading');
            setRetryAttempt((attempt) => attempt + 1);
          }}
        >
          Retry CAPTCHA
        </button>
      )}
    </div>
  );
}
